import { memo, useRef, useState, useCallback } from 'react';

import { FiChevronLeft, FiChevronRight, FiRadio } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UseQueryResult } from 'react-query';
import { AxiosError } from 'axios';

import {
  RoomDataWithSpeakers,
  RoomsWithSpeakersResult,
} from '@common/types/api';
import { formatRoomDisplayData } from '@utils';

import * as S from './styles';

type BroadcastRoomsCarouselProps = {
  roomsQuery: UseQueryResult<RoomsWithSpeakersResult, AxiosError>;
};

const getStatusVariant = (
  room: RoomDataWithSpeakers,
): 'live' | 'soon' | 'reprise' => {
  const data = formatRoomDisplayData(room);
  if (data.isOnline) return 'live';
  if (data.status === 'pages.rooms.soon') return 'soon';
  return 'reprise';
};

const RoomCardItem = ({
  room,
  statusLabel,
}: {
  room: RoomDataWithSpeakers;
  statusLabel: string;
}) => {
  const { day, month } = formatRoomDisplayData(room);

  return (
    <S.RoomCard key={room.slug}>
      <Link to={`/broadcast/${room.slug}`}>
        <S.RoomThumb>
          {room.thumb_image && <img src={room.thumb_image} alt={room.name} />}
        </S.RoomThumb>
        <S.RoomInfo>
          <S.DayBadge>
            <span>{day}</span>
            <span>{month}</span>
          </S.DayBadge>
          <S.RoomDetails>
            <h3>{room.name}</h3>
            <S.StatusBadge $variant={getStatusVariant(room)}>
              {getStatusVariant(room) === 'live' && <S.PulseDot />}
              {statusLabel}
            </S.StatusBadge>
          </S.RoomDetails>
        </S.RoomInfo>
      </Link>
    </S.RoomCard>
  );
};

const BroadcastRoomsCarousel = ({
  roomsQuery,
}: BroadcastRoomsCarouselProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(true);
  const { t } = useTranslation();

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, []);

  const scroll = (dir: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const firstCard = el.querySelector(':scope > *') as HTMLElement;
    const cardW = firstCard ? firstCard.offsetWidth : 280;
    const amount = cardW + 16;
    el.scrollBy({
      left: dir === 'left' ? -amount : amount,
      behavior: 'smooth',
    });
    setTimeout(checkScroll, 350);
  };

  if (!roomsQuery.isSuccess || roomsQuery.data.rooms.length <= 1) {
    return null;
  }

  const rooms = roomsQuery.data.rooms.slice(1);

  return (
    <div style={{ marginBottom: '2.95rem' }}>
      <S.Header>
        <S.HeaderLeft>
          <FiRadio size={20} />
          <h2>{t('pages.lobby.rooms')}</h2>
          {/* {activeCount > 0 && (
            <S.ActiveBadge>
              {activeCount} {t('pages.lobby.activeRooms') || 'ativas'}
            </S.ActiveBadge>
          )} */}
        </S.HeaderLeft>
        {rooms.length > 3 && (
          <S.ShowAllLink to="/rooms">
            {t('pages.lobby.showMoreRooms')}
          </S.ShowAllLink>
        )}
      </S.Header>

      <S.CarouselWrapper>
        {canLeft && (
          <S.Arrow $direction="left" onClick={() => scroll('left')}>
            <FiChevronLeft size={14} />
          </S.Arrow>
        )}
        {canRight && rooms.length > 3 && (
          <S.Arrow $direction="right" onClick={() => scroll('right')}>
            <FiChevronRight size={14} />
          </S.Arrow>
        )}

        <S.ScrollContainer ref={scrollRef} onScroll={checkScroll}>
          {rooms.map((room) => (
            <RoomCardItem
              key={room.slug}
              room={room}
              statusLabel={t(formatRoomDisplayData(room).status)}
            />
          ))}
        </S.ScrollContainer>
      </S.CarouselWrapper>
    </div>
  );
};

export default memo(BroadcastRoomsCarousel);
